<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';

  /**
   * One row of «Más ajustes» (plan §5.12): the title and a one-line summary,
   * expanding in place. The content mounts the first time the row opens
   * (lazy loads) and stays mounted afterwards, so closing it never loses
   * what was typed. Plan-gated rows show the «Plus» badge and the upsell
   * instead of expanding.
   */
  const props = withDefaults(
    defineProps<{
      title: string;
      summary?: string | null;
      /** The summary's tone: amber for a warning or pending work. */
      tone?: 'neutral' | 'amber';
      /** The role could, but the plan lacks it. */
      upsell?: boolean;
      /** `/{stringId}` of the plan link. */
      stringId?: string | null;
      defaultOpen?: boolean;
    }>(),
    {
      summary: null,
      tone: 'neutral',
      upsell: false,
      stringId: null,
      defaultOpen: false,
    }
  );

  const emit = defineEmits<{ (e: 'open'): void }>();

  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string) => String(proxy.$t(key));

  const panelId = `catalog-disclosure-${Math.random()
    .toString(36)
    .slice(2, 10)}`;

  const expanded = ref(props.defaultOpen && !props.upsell);
  /** Mounted once, then kept: closing hides it. */
  const rendered = ref(expanded.value);

  const planPath = computed(() =>
    props.stringId ? `/${props.stringId}/settings?tab=plan` : null
  );

  function toggle() {
    expanded.value = !expanded.value;
    if (expanded.value && !rendered.value) {
      rendered.value = true;
      emit('open');
    }
  }
</script>

<template>
  <div class="v2-catalog-settings-disclosure" :class="{ 'is-open': expanded }">
    <div v-if="upsell" class="disclosure-head is-static">
      <span class="head-text">
        <span class="head-title-line">
          <span class="head-title">{{ title }}</span>
          <span class="head-plan">Plus</span>
        </span>
        <span class="head-summary">
          {{ t('menuPage.upsell.plus') }} ·
          <router-link v-if="planPath" :to="planPath" class="upsell-link">{{
            t('menuPage.upsell.cta')
          }}</router-link>
        </span>
      </span>
    </div>
    <button
      v-else
      type="button"
      class="disclosure-head"
      :aria-expanded="expanded ? 'true' : 'false'"
      :aria-controls="panelId"
      @click="toggle"
    >
      <span class="head-text">
        <span class="head-title">{{ title }}</span>
        <span v-if="summary" class="head-summary" :class="`is-${tone}`">{{
          summary
        }}</span>
      </span>
      <i class="mdi mdi-chevron-down head-chevron" aria-hidden="true" />
    </button>
    <div
      v-if="rendered"
      v-show="expanded"
      :id="panelId"
      class="disclosure-body"
    >
      <slot />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-settings-disclosure {
    @apply tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100;

    &:first-child {
      @apply tw-border-t-0;
    }

    .disclosure-head {
      @apply tw-flex tw-min-h-[56px] tw-w-full tw-cursor-pointer tw-items-center tw-border-0 tw-bg-transparent tw-px-4 tw-py-2 tw-text-left;

      &:hover {
        @apply tw-bg-neutral-50;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-inset tw-ring-orange-500/40;
      }

      &.is-static {
        @apply tw-cursor-default;

        &:hover {
          @apply tw-bg-transparent;
        }
      }
    }

    .head-text {
      @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col;
    }

    .head-title-line {
      @apply tw-flex tw-items-center;
    }

    .head-title {
      @apply tw-text-sm tw-font-medium tw-text-neutral-900;
    }

    .head-plan {
      @apply tw-ml-2 tw-rounded-full tw-bg-neutral-100 tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-bold tw-uppercase tw-leading-none tw-tracking-wide tw-text-neutral-600;
    }

    .head-summary {
      @apply tw-mt-0.5 tw-text-xs tw-leading-5 tw-text-neutral-500;

      &.is-amber {
        @apply tw-font-medium tw-text-amber-800;
      }

      .upsell-link {
        @apply tw-relative tw-font-semibold tw-text-neutral-900 tw-underline;

          // A 44 px tall hit area around the inline link.
          &::after {
            content: '';
            position: absolute;
            top: -12px;
            bottom: -12px;
            left: -4px;
            right: -4px;
          }
      }
    }

    .head-chevron {
      @apply tw-ml-2 tw-flex-none tw-text-xl tw-text-neutral-400 tw-transition-transform;
    }

    &.is-open .head-chevron {
      @apply tw-rotate-180;
    }

    .disclosure-body {
      @apply tw-px-4 tw-pb-4 tw-pt-1;
    }
  }
</style>
